import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePDF } from "react-to-pdf";
import { Box, Typography } from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";

import MiniDrawer, { closedDrawerWidth, drawerWidth } from "./Drawer";
import AppBar from "./AppBar";
import {
  SuperAdminSideMenuItems,
  StandardAdminSideMenuItems,
} from "../../utils/SideMenuItems";
import { isSuperAdmin } from "../../utils/Roles";
import { DrawerHeader } from "./Drawer/components/StyledDrawerHeader";
import Button from "../Button";
import messages from "../../assets/locales/messages";

const Layout = () => {
  const { isRtl, locale } = useSelector((state) => state.locale);
  const { appBarTitle } = useSelector((state) => state.appHelpers);
  const { appBarContent, appBarMobileContent, exportedPdfTarget } = useSelector(
    (state) => state.appHelpers
  );
  const { shared } = messages[locale];

  // initially false for mobile view
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const [pdfFileOptions, setPdfFileOptions] = useState({
    filename: "owner-details.pdf",
    method: "save",
    page: {},
    overrides: {
      canvas: {
        backgroundColor: "#041937",
      },
    },
  });
  const { toPDF, targetRef } = usePDF(pdfFileOptions);

  useEffect(() => {
    if (exportedPdfTarget?.pdfOptions?.filename) {
      setPdfFileOptions((prev) => ({
        ...prev,
        filename: exportedPdfTarget?.pdfOptions?.filename,
      }));
    }
  }, [exportedPdfTarget?.pdfOptions?.filename]);

  const exportPdfButton = (
    <Box
      sx={{
        display: { xs: "none", md: "block" },
      }}>
      <Button
        label={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PictureAsPdf fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6">{shared.exportPdf}</Typography>
          </Box>
        }
        onClick={() => toPDF()}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        overflowX: "auto",
        overflowY: "hidden",
        minHeight: "100vh",
      }}>
      <AppBar
        drawerOpen={drawerOpen}
        handleDrawerOpen={handleDrawerOpen}
        appBarTitle={appBarTitle}
        appBarContent={appBarContent}
        appBarMobileContent={appBarMobileContent}
        exportPdfButton={exportPdfButton}
      />
      <MiniDrawer
        handleDrawerToggle={handleDrawerToggle}
        open={drawerOpen}
        isRtl={isRtl}
        sideMenuItems={
          isSuperAdmin() ? SuperAdminSideMenuItems : StandardAdminSideMenuItems
        }
      />
      <Box
        ref={targetRef}
        sx={{
          pt: { xs: 2, sm: 4 },
          pr: { xs: 2, sm: "2rem" },
          pl: { xs: 2, sm: "2rem" },
          ml: {
            xs: "auto",
            md: drawerOpen
              ? `calc(${drawerWidth}px)`
              : `calc(${closedDrawerWidth})`,
          },
          mr: { xs: "auto", md: 0 },
          width: {
            xs: "100%",
            md: drawerOpen ? `calc(100vw - ${drawerWidth}px - 15px)` : `calc(100vw - ${closedDrawerWidth} - 15px)`,
          },
          transition: (theme) =>
            theme.transitions.create(["margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
